import React, { useMemo, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation, useParams, useSearchParams } from "react-router-dom";
import { FormEngine, RENDER_MODE, RENDER_TYPE, isTimezoneDateField } from "@fieldEngine";
import { PATHS } from "@/router/paths";
import {
  createActivityRequest,
  resetCreateActivity,
  updateActivityRequest,
  resetUpdateActivity,
} from "./store/activityFormSlice";
import {
  selectCreateActivityError,
  selectCreateActivityFieldErrors,
  selectCreateActivityResult,
  selectCreatedActivityId,
  selectIsCreatingActivity,
  selectUpdateActivityError,
  selectUpdateActivityFieldErrors,
  selectUpdateActivityResult,
  selectUpdatedActivityId,
  selectIsUpdatingActivity,
} from "./store/selectors";
import { mapBackendFieldErrors } from "../utils/mapBackendFieldErrors";
import { useActivityTemplates } from "./hooks/useActivityTemplates";
import { useActivityFields } from "./hooks/useActivityFields";
import { useSkillsetTags } from "./hooks/useSkillsetTags";
import { useCurrentUserOwner } from "./hooks/useCurrentUserOwner";
import { useActivityHydration } from "./hooks/useActivityHydration";
import { useStaticFieldEnrichments } from "./config/staticFieldEnrichments";
import { buildEditInitialValues } from "./utils/buildEditInitialValues";
import {
  ACTIVITY_FILE_SETTINGS,
  isFileField,
} from "./config/fileFieldSettings";
import { sortBySequence } from "./utils/sortBySequence";
import { buildActivityPayload } from "./utils/buildActivityPayload";
import {
  ACTIVITY_FIELD_KEY,
  DEFAULT_ACTIVITY_RETURN_URL,
} from "./constants";
import { getWorkkardDetailMasterData } from "../../WorkkardDetail/actions/commonAction";
import TemplateSelector from "./components/TemplateSelector";
import { CreateActivityLoader } from "../../../components/SkeletonLoader";
import LoaderComponent from "../../../components/Loader";
import DialogueBox from "../../../components/DialogueBox";
import ConfirmationPrompt from "../../../components/ConfirmationPrompt";
import Toast from "../../../components/Toast";
import { showToast, hideToast } from "../../SpockViewPage/actions/action";
import DateTimezoneAdornment from "../AddEditWorkkard/components/DateTimezoneAdornment";
import { setTimezoneRequest, clearTimezone } from "../AddEditWorkkard/store/timezoneSlice"
import hiddenFieldsConfig from "./config/hiddenFields.json";
import "./styles/CreateActivity.scss";
import { stripHashSuffix } from "./utils/buildActivityPayload";

const { hiddenFieldNameFragments } = hiddenFieldsConfig;

const SYSTEM_FIELD_SEQUENCE = Object.freeze({
  "Activity Title": 1,
  "Activity Description": 2,
  "Activity Due Date": 3,
  "Activity Owner": 4,
  "Choose a skillset": 9998,
  "Choose an assignment method": 9999,
});

const ATTACH_FILE_BASE_SEQUENCE = 10000;

const STATIC_ACTIVITY_FIELD_ERROR_MAP = {
  activitytitle: ACTIVITY_FIELD_KEY.TITLE,
  activityowner: ACTIVITY_FIELD_KEY.OWNER,
  activityduedate: ACTIVITY_FIELD_KEY.DUE_DATE,
  activitydescription: ACTIVITY_FIELD_KEY.DESCRIPTION,
  skillset: ACTIVITY_FIELD_KEY.SKILLSET,
};

const DISABLED_SYSTEM_FIELDS = [
  ACTIVITY_FIELD_KEY.OWNER,
  ACTIVITY_FIELD_KEY.SKILLSET,
  ACTIVITY_FIELD_KEY.ASSIGNMENT
]


function isHiddenField(row) {
  const searchable = `${row.fieldName || ""} ${row.fieldKey || ""}`.toLowerCase();
  return hiddenFieldNameFragments.some((f) => searchable.includes(f));
}

function enrichSystemField(row, enrichments, isEdit=false) {
  const rawName = String(row.fieldName || "").trim();
  const key = rawName.toLowerCase();
  const cfg = enrichments[key];
  if (!cfg) return null;
  const forcedSequence = SYSTEM_FIELD_SEQUENCE[rawName] ?? row.sequence;
  return {
    sequence: forcedSequence,
    customFieldId: row.customFieldId ?? "0",
    fieldName: cfg.fieldKey,
    fieldKey: cfg.fieldKey,
    fieldType: cfg.fieldType,
    label: cfg.label,
    description: cfg.description ?? "",
    items: cfg.items ?? [],
    isRequired: !!cfg.isRequired,
    validation: cfg.isRequired && cfg.requiredMessage
      ? [{ rule: "required", message: cfg.requiredMessage }]
      : undefined,
    maxLength: row.maxLength ?? row.max_length ?? row.MaxLength ?? null,
    FieldSettings: cfg.fieldSettings ?? null,
    isNonEditable: isEdit && DISABLED_SYSTEM_FIELDS.includes(cfg.fieldKey),
    isSystemField: true,
    group: "",
    fieldGroupName: "",
    value: "",
    preInitScript: "",
    presaveScript: "",
    settings: "",
  };
}

function buildDefaultRows(enrichments, isEdit=false) {
  return Object.entries(SYSTEM_FIELD_SEQUENCE)
    .map(([fieldName, sequence]) =>
      enrichSystemField({ fieldName, sequence: String(sequence) }, enrichments, isEdit),
    )
    .filter(Boolean);
}

function buildMapper(fields) {
  const map = {};
  (fields || []).forEach((cf) => {
    if (!cf) return;
    const key = cf.FieldKey || cf.fieldKey;
    if (key) map[key] = cf.Value ?? cf.value;
  });
  return map;
}

function AddEditActivity({
  workkardId: propWorkkardId,
  tmplId,
  wrrId: wrrIdProp = 0,
  wrkdReopenSubreasonId: wrkdReopenSubreasonIdProp = 0,
  isEdit: propIsEdit = false,
  isClone: propIsClone = false,
  actId: propActId,
  referrer: propReferrer = "",
  onClose,
}) {
  const routeParams = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const workkardId = propWorkkardId || routeParams.workkardId;
  const actId = propActId || routeParams.actId;
  const isEdit = propIsEdit || !!actId;
  const isClone = propIsClone || !!location.state?.isClone;
  const wrrId =
    wrrIdProp || location.state?.wrrId || searchParams.get("wrrId") || 0;
  const wrkdReopenSubreasonId =
    wrkdReopenSubreasonIdProp ||
    location.state?.wrkdReopenSubreasonId ||
    (searchParams.has("wrsId") ? searchParams.get("wrsId") : 0);
  const dispatch = useDispatch();

  const createError = useSelector(selectCreateActivityError);
  const createFieldErrors = useSelector(selectCreateActivityFieldErrors);
  const createResult = useSelector(selectCreateActivityResult);
  const createdActivityId = useSelector(selectCreatedActivityId);
  const isCreating = useSelector(selectIsCreatingActivity);

  const updateError = useSelector(selectUpdateActivityError);
  const updateFieldErrors = useSelector(selectUpdateActivityFieldErrors);
  const updateResult = useSelector(selectUpdateActivityResult);
  const updatedActivityId = useSelector(selectUpdatedActivityId);
  const isUpdating = useSelector(selectIsUpdatingActivity);

  const toast = useSelector((state) => state.toast);

  // Stays true across the whole write: the POST, and then the wait for the
  // activity to become readable. Redux owns it because FormEngine's own
  // isSubmitting only spans the synchronous dispatch.
  const isSubmitting = isEdit && !isClone ? isUpdating : isCreating;
  const saveError = isEdit && !isClone ? updateError : createError;

  const loadedWorkkardId = useSelector((state) => state.workkardDetail?.workkardDetail?.Id);

  useEffect(() => {
    if (workkardId && String(loadedWorkkardId) !== String(workkardId)) {
      dispatch(getWorkkardDetailMasterData({ cId: workkardId }));
    }
  }, [workkardId, loadedWorkkardId, dispatch]);

  const { activityData, loading: isLoadingActivity, error: fetchActivityError } =
    useActivityHydration(isEdit && !isClone ? actId : null);

  const filteredVals = useSelector((state) =>
    (state.workkardDetail?.workkardDetail?.CustomFields || []).filter(
      (cf) => cf?.FieldGroup === "Property Location",
    ),
  );
  const tzText = useSelector((state) =>
    isEdit
      ? activityData?.due_date_tz
      : state.sessionContextOidc?.userView?.timezone,
  );
  const tzList = useSelector((state) => state.sessionContextOidc?.tzData ?? []);
  const tzValue = tzList?.find((tz) => tz.standard_time_zone === tzText || tz.mapped_time_zone === tzText)?.id;
  const defaultValMap = buildMapper(filteredVals);

 useEffect(() => {
    const zip = String(defaultValMap?.propertylocation_zip_zip ?? "").trim();
    if (!zip) {
      dispatch(clearTimezone());
      return;
    }
    dispatch(setTimezoneRequest({ zip }));
 }, [defaultValMap?.propertylocation_zip_zip, dispatch]);

  const {
    templates,
    areTemplatesLoading,
    selectedTemplateShortName,
    setSelectedTemplateShortName,
    selectedTemplateName,
  } = useActivityTemplates(tmplId || location?.state?.tmplId);

  const [isFormDirty, setIsFormDirty] = useState(false);
  const [pendingTemplateShortName, setPendingTemplateShortName] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const handleTemplateSelect = (shortName) => {
    if (isFormDirty) {
      setPendingTemplateShortName(shortName);
    } else {
      setSelectedTemplateShortName(shortName);
    }
  };

  const confirmTemplateChange = () => {
    setSelectedTemplateShortName(pendingTemplateShortName);
    setPendingTemplateShortName(null);
  };

  const cancelTemplateChange = () => setPendingTemplateShortName(null);

  useEffect(() => {
    if (isEdit && !isClone && activityData?.template_name && !selectedTemplateShortName) {
      setSelectedTemplateShortName(activityData?.template_name);
    }
  }, [isEdit, isClone, activityData, selectedTemplateShortName, setSelectedTemplateShortName]);

  const renderType = isEdit && !isClone ? "edit" : "new";
  const { activityFields, areFieldsLoading } = useActivityFields(
    selectedTemplateShortName,
    renderType,
  );
  const { currentUser, currentUserId, users } = useCurrentUserOwner();
  useSkillsetTags();

  const initialValues = useMemo(() => {
    if (isEdit && !isClone && activityData) {
      return buildEditInitialValues(activityData, activityFields);
    }

    return {
      [ACTIVITY_FIELD_KEY.TITLE]: selectedTemplateName ?? "",
      ...(currentUserId ? { [ACTIVITY_FIELD_KEY.OWNER]: currentUserId } : {}),
      ...((!isEdit && defaultValMap) || {}),
      ...(!isEdit && tzValue ? { [ACTIVITY_FIELD_KEY.DUE_DATE_TZ]: tzValue } : {}),
    };
  }, [
    isEdit,
    isClone,
    activityData,
    activityFields,
    selectedTemplateName,
    currentUserId,
    defaultValMap,
    tzValue,
  ]);

  const staticEnrichments = useStaticFieldEnrichments({
    users,
    currentUserId,
    currentUser,
    isEdit: isEdit && !isClone,
    initialValues,
    ...(isEdit && tzValue && { defaultValue: tzValue }),
  });

  const templateFormRows = useMemo(() => {
    if (!selectedTemplateShortName) return buildDefaultRows(staticEnrichments, isEdit);

    let fileCounter = 0;
    const rows = activityFields
      .filter((row) => !isHiddenField(row))
      .map((row) => {
        if (row.isSystemField) return enrichSystemField(row, staticEnrichments, isEdit);

        if (isTimezoneDateField(row)) {
          return {
            ...row,
            FieldSettings: {
              ...(row.FieldSettings || {}),
              rightAdornment: () => <DateTimezoneAdornment />,
            },
          };
        }

        if (isFileField(row)) {
          return {
            ...row,
            sequence: ATTACH_FILE_BASE_SEQUENCE + fileCounter++,
            FieldSettings: {
              colWidth: 9,
              ...(row.FieldSettings || {}),
              ...ACTIVITY_FILE_SETTINGS,
            },
          };
        }

        return row;
      })
      .filter(Boolean);

    const needsSystemDefaults = buildDefaultRows(staticEnrichments, isEdit).filter(
      (defaultRow) => !rows.some((r) => r.fieldKey === defaultRow.fieldKey),
    );

    return sortBySequence([...rows, ...needsSystemDefaults]);
  }, [selectedTemplateShortName, staticEnrichments, activityFields, isEdit, isClone]);

  const returnUrl = location.state?.returnUrl ?? `${DEFAULT_ACTIVITY_RETURN_URL}${workkardId}`;

  const isTrueEdit = isEdit && !isClone;

  const backendFieldErrors = useMemo(
    () => mapBackendFieldErrors(
      isTrueEdit ? updateFieldErrors : createFieldErrors,
      activityFields,
      STATIC_ACTIVITY_FIELD_ERROR_MAP,
    ),
    [isTrueEdit, updateFieldErrors, createFieldErrors, activityFields],
  );

  const doCancel = () => {
    if (onClose) onClose();
    else navigate(returnUrl);
  };

  const handleCancel = () => {
    if (isTrueEdit || isFormDirty) {
      setShowCancelConfirm(true);
    } else {
      doCancel();
    }
  };

  const confirmCancel = () => {
    setShowCancelConfirm(false);
    doCancel();
  };

  const dismissCancelConfirm = () => setShowCancelConfirm(false);

  const handleFormSubmit = (formData) => {
    const payload = buildActivityPayload({
      formData,
      templateId: selectedTemplateName ?? selectedTemplateShortName,
      workkardId,
      wrrId,
      wrkdReopenSubreasonId,
      activityId: actId,
      isEdit: isEdit && !isClone,
      activityFields,
      tzList,
      fallbackDueDateTz: tzList.find((tz) => tz.id === tzValue)?.mapped_time_zone || '',
    });

    if (isEdit && !isClone) {
      dispatch(updateActivityRequest(payload));
    } else {
      dispatch(createActivityRequest(payload));
    }
  };

  // The created activity is readable -- either its socket event arrived or the
  // poll fallback found it. Only now is it safe to open the detail page.
  useEffect(() => {
    if (!createdActivityId) return;
    dispatch(resetCreateActivity());
    dispatch(showToast({ message: 'Activity created successfully.', type: 'success', delayTime: 5000, autoClose: true }));
    if (onClose) onClose(createdActivityId);
    else navigate(`${PATHS.WORKKARD_DETAIL}?cId=${workkardId}&actId=${createdActivityId}#an`);
  }, [createdActivityId, dispatch, navigate, onClose, workkardId]);

  useEffect(() => {
    if (!updatedActivityId) return;
    dispatch(resetUpdateActivity());
    dispatch(showToast({ message: 'Activity updated successfully.', type: 'success', delayTime: 5000, autoClose: true }));
    if (onClose) onClose(updatedActivityId);
    else navigate(`${PATHS.WORKKARD_DETAIL}?cId=${workkardId}&actId=${updatedActivityId}#an`);
  }, [updatedActivityId, dispatch, navigate, onClose, workkardId]);

  // Fallback for a response that carried no activity id, where there is nothing
  // to wait on.
  useEffect(() => {
    if (createResult) {
      dispatch(resetCreateActivity());
      dispatch(showToast({ message: 'Activity created successfully.', type: 'success', delayTime: 5000, autoClose: true }));
      if (onClose) onClose(createResult?.Id);
      else navigate(returnUrl);
    }
  }, [createResult, dispatch, navigate, onClose, returnUrl, workkardId]);

  useEffect(() => {
    if (updateResult) {
      dispatch(resetUpdateActivity());
      dispatch(showToast({ message: 'Activity updated successfully.', type: 'success', delayTime: 5000, autoClose: true }));
      if (onClose) onClose(actId);
      else navigate(returnUrl);
    }
  }, [updateResult, dispatch, navigate, onClose, returnUrl, actId, workkardId]);

  // TODO: error toast disabled for now, revisit and implement later. Under-field
  // error display (backendFieldErrors via mapBackendFieldErrors) still applies.
  useEffect(() => {
    if (createError) {
      console.error("[AddEditActivity] Create failed:", createError);
      // dispatch(showToast({ message: createError, type: 'error', delayTime: 5000, autoClose: true }));
    }
  }, [createError, dispatch]);

  useEffect(() => {
    if (updateError) {
      console.error("[AddEditActivity] Update failed:", updateError);
      // dispatch(showToast({ message: updateError, type: 'error', delayTime: 5000, autoClose: true }));
    }
  }, [updateError, dispatch]);

  useEffect(() => {
    if (fetchActivityError) {
      console.error("[AddEditActivity] Fetch activity failed:", fetchActivityError);
      // dispatch(showToast({ message: fetchActivityError, type: 'error', delayTime: 5000, autoClose: true }));
    }
  }, [fetchActivityError, dispatch]);

  // Reset on mount as well as unmount: the saga outlives the form, so a wait the
  // user navigated away from can land a readyActivityId in a slice nobody is
  // watching, and the next mount would redirect on it immediately.
  useEffect(
    () => {
      dispatch(resetCreateActivity());
      dispatch(resetUpdateActivity());
      return () => {
        dispatch(resetCreateActivity());
        dispatch(resetUpdateActivity());
      };
    },
    [dispatch],
  );

  if (isEdit && !isClone && isLoadingActivity) {
    return (
      <div className="activity-creation-page" data-test="activity-form-page">
        <CreateActivityLoader count={1} />
      </div>
    );
  }

  return (
    <div className="activity-creation-page" data-test="activity-form-page">
      {(!isEdit || isClone) && (
        <TemplateSelector
          templates={templates}
          selectedTemplateShortName={selectedTemplateShortName}
          onSelect={handleTemplateSelect}
          disabled={areTemplatesLoading}
        />
      )}

      <div className="activity-creation-content-body">
        {areFieldsLoading ? (
          <CreateActivityLoader count={1} />
        ) : isEdit && !isClone && !activityData ? (
          <CreateActivityLoader count={1} />
        ) : (
          <FormEngine
            key={`${selectedTemplateShortName ?? "default"}::${currentUserId}`}
            templateId={selectedTemplateShortName}
            templateShortName={selectedTemplateName ?? selectedTemplateShortName}
            rawRows={templateFormRows}
            initialValues={initialValues}
            renderMode={isEdit && !isClone ? RENDER_MODE.EDIT : RENDER_MODE.CREATE}
            renderType={RENDER_TYPE.ACTIVITY ?? "activity"}
            onSubmit={handleFormSubmit}
            onCancel={handleCancel}
            submitLabel={isEdit && !isClone ? "Update" : "Save"}
            cancelLabel="Cancel"
            // The write is not done when the POST returns -- the dots stay up
            // until the activity is readable, which can be several seconds, so
            // the Redux flag drives the button rather than FormEngine's own.
            isSubmitting={isSubmitting}
            submittingLabel={<LoaderComponent />}
            // separator={<span className="activity-creation-actions-row-or">or</span>}
            onDirtyChange={setIsFormDirty}
            fieldErrors={backendFieldErrors}
            footer={
              saveError && (
                <div className="activity-creation-save-error" role="alert" data-test="activity-form-save-error">
                  {typeof saveError === "string"
                    ? saveError
                    : "Something went wrong while saving. Please try again."}
                </div>
              )
            }
          />
        )}
      </div>

      <DialogueBox
        overlayClassName="modal"
        contentClassName="modalContent animated faster fadeInUpx2 small"
        shouldCloseOnOverlayClick={false}
        isOpen={!!pendingTemplateShortName}
        onRequestClose={cancelTemplateChange}
      >
        <ConfirmationPrompt
          message="You are trying to  change the template, all the entered data will be lost."
          onConfirmClick={confirmTemplateChange}
          onCancelClick={cancelTemplateChange}
          confirmationText="Confirm"
          cancelText="Cancel"
          class="modalBody"
          footerClass="modalFooter"
        />
      </DialogueBox>

      <DialogueBox
        overlayClassName="modal"
        contentClassName="modalContent animated faster fadeInUpx2 small"
        shouldCloseOnOverlayClick={false}
        isOpen={showCancelConfirm}
        onRequestClose={dismissCancelConfirm}
      >
        <ConfirmationPrompt
          message={isTrueEdit
            ? "Do you really want to cancel the edit process of this Activity?"
            : "Do you really want to cancel the Activity creation process? All the populated data will be lost."}
          onConfirmClick={confirmCancel}
          onCancelClick={dismissCancelConfirm}
          confirmationText="Confirm"
          cancelText="Cancel"
          class="modalBody"
          footerClass="modalFooter"
        />
      </DialogueBox>

      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          delayTime={toast.delayTime}
          closeToast={() => dispatch(hideToast())}
          show={toast.show}
          entityType={toast.entityType}
          cId={toast.cId}
          actId={toast.actId}
          showActions={toast.showActions}
          isCloseIcon={toast.isCloseIcon}
          autoClose={toast.autoClose}
        />
      )}
    </div>
  );
}

export default AddEditActivity;
